/**
 * Social — kleed filmen voor Instagram (plan 2026-09-01-kleda-instagram-systeem.md)
 *
 * Twee blokken:
 * (a) Kleedkiezer: zoek op nummer (zelfde zoekpad als ZoekPage, alle fases) en
 *     open de camera met fase 'social' en een langere opnamegrens (180s). De
 *     clip landt onder het kleed, blijft buiten de klant-timeline (allowlist
 *     KLANT_FASEN) en gaat 's ochtends mee naar de selector.
 * (b) "Vandaag voor social": wat er vandaag gemarkeerd (sterretje) of gefilmd is,
 *     zodat de wasserij ziet wat er morgen op tafel ligt.
 *
 * Bewust geen eigen wachtrij of status: de mediaQueue doet de upload, de
 * selector doet de keuze. Deze pagina is alleen de ingang.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, Badge } from '../components/ui';
import { CameraCapture } from '../components/CameraCapture';
import { RemoteCameraPanel } from '../components/RemoteCameraPanel';
import { salesforceClient } from '../lib/salesforce';
import type { ZoekKleed } from '../lib/salesforce';
import { useAuthStore } from '../stores';

const MIN_CIJFERS = 3;
const DEBOUNCE_MS = 400;
/** Social-clips mogen langer dan de 60s van de werkfases. */
const SOCIAL_MAX_SECONDS = 180;

interface SocialMedia {
  id: string;
  url: string;
  type: 'foto' | 'video';
  fase: string;
  wi_sf_id: string | null;
  taken_at: string;
  social_kandidaat?: boolean;
}

function vandaagIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function SocialPage() {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<ZoekKleed[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gezocht, setGezocht] = useState(false);
  const [filmen, setFilmen] = useState<ZoekKleed | null>(null);
  const [vasteCamera, setVasteCamera] = useState<ZoekKleed | null>(null);
  const [cameraPilot, setCameraPilot] = useState(false);
  const [vandaag, setVandaag] = useState<SocialMedia[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const volgnummer = useRef(0);

  const cijfers = query.replace(/\D/g, '');
  const teKort = cijfers.length > 0 && cijfers.length < MIN_CIJFERS;

  const zoek = useCallback(async (term: string) => {
    const mijn = ++volgnummer.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await salesforceClient.zoekKleden(term);
      if (mijn !== volgnummer.current) return;
      if (response.success && response.data) {
        setItems(response.data.items ?? []);
      } else {
        setItems([]);
        setError(response.message || 'Zoeken lukt nu niet. Probeer het zo opnieuw.');
      }
    } catch (err) {
      if (mijn !== volgnummer.current) return;
      setItems([]);
      setError(err instanceof Error && err.message ? err.message : 'Zoeken lukt nu niet.');
    } finally {
      if (mijn === volgnummer.current) {
        setIsLoading(false);
        setGezocht(true);
      }
    }
  }, []);

  useEffect(() => {
    if (cijfers.length < MIN_CIJFERS) {
      volgnummer.current++;
      setItems([]);
      setGezocht(false);
      setError(null);
      setIsLoading(false);
      return;
    }
    const timer = setTimeout(() => { void zoek(query); }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, cijfers.length, zoek]);

  // Vandaag gemarkeerd of gefilmd — twee filters op GET /api/media, samengevoegd.
  useEffect(() => {
    let cancelled = false;
    async function laad() {
      const { staff, pin } = useAuthStore.getState();
      if (!staff?.id || !pin) return;
      const headers = { 'X-Staff-Id': staff.id, 'X-Staff-Pin': pin };
      const sinds = encodeURIComponent(vandaagIso());
      try {
        const [a, b] = await Promise.all([
          fetch(`/api/media?sinds=${sinds}&fase=social`, { headers }),
          fetch(`/api/media?sinds=${sinds}&kandidaat=true`, { headers }),
        ]);
        const lijst: SocialMedia[] = [];
        for (const res of [a, b]) {
          if (!res.ok) continue;
          const data = (await res.json()) as { success: boolean; media: SocialMedia[] };
          if (data.success) lijst.push(...data.media);
        }
        const uniek = new Map(lijst.map((m) => [m.id, m]));
        if (!cancelled) {
          setVandaag(
            [...uniek.values()].sort((x, y) => y.taken_at.localeCompare(x.taken_at))
          );
        }
      } catch {
        if (!cancelled) setVandaag([]);
      }
    }
    void laad();
    // Nalees na een capture-sessie: de upload-queue kan nog bezig zijn.
    const timer = setTimeout(() => void laad(), 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [refreshKey]);

  if (filmen) {
    return (
      <CameraCapture
        fase="social"
        doel={{ wiSfId: filmen.itemId }}
        subtitle={filmen.itemNaam ?? undefined}
        captureStatus={filmen.status ?? undefined}
        maxSeconds={SOCIAL_MAX_SECONDS}
        onClose={() => {
          setFilmen(null);
          setRefreshKey((k) => k + 1);
        }}
      />
    );
  }

  return (
    <div className="space-y-lg">
      <section>
        <div className="flex items-center gap-sm mb-xs">
          <Link to="/" className="p-xs -ml-xs rounded-kleda-sm hover:bg-kleda-bg-clean transition-colors">
            <svg className="w-5 h-5 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h2 className="font-heading text-xl font-bold text-kleda-text-main">Social</h2>
            <p className="text-kleda-text-muted">Mooi kleed? Film het voor Instagram</p>
          </div>
        </div>
      </section>

      <Card>
        <CardContent className="py-md">
          <p className="text-sm font-semibold text-kleda-text-main mb-xs">Zo wordt het een goede clip</p>
          <ul className="text-sm text-kleda-text-muted space-y-xs list-disc pl-md">
            <li>Het hele kleed in beeld, toestel stil houden (of langzaam over het kleed bewegen)</li>
            <li>Film een volledige handeling, zoals het inborstelen. Opnemen kan hier tot 3 minuten.</li>
            <li>Geen labels met klantnaam of adres in beeld</li>
          </ul>
          <p className="text-xs text-kleda-text-muted mt-sm">
            Sneller: zet een sterretje op een foto in de werklijst. Dat telt ook.
          </p>
        </CardContent>
      </Card>

      {/* Kleedkiezer */}
      <RemoteCameraPanel wiSfId={vasteCamera?.itemId} wiName={vasteCamera?.itemNaam ?? undefined} onAvailable={setCameraPilot} />
      <div className="relative">
        <svg
          className="absolute left-md top-1/2 -translate-y-1/2 w-5 h-5 text-kleda-text-muted pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="WI-nummer van het kleed"
          className="w-full pl-12 pr-12 py-md text-lg rounded-kleda-md border border-gray-200 bg-white text-kleda-text-main placeholder:text-kleda-text-muted focus:outline-none focus:ring-2 focus:ring-kleda-primary focus:border-transparent"
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            aria-label="Wis zoekopdracht"
            className="absolute right-md top-1/2 -translate-y-1/2 p-xs rounded-full hover:bg-kleda-bg-clean"
          >
            <svg className="w-4 h-4 text-kleda-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {teKort && <p className="text-sm text-kleda-text-muted">Typ minstens {MIN_CIJFERS} cijfers.</p>}
      {isLoading && <p className="text-sm text-kleda-text-muted">Zoeken...</p>}
      {!isLoading && error && <p className="text-sm text-red-600">{error}</p>}
      {!isLoading && !error && gezocht && items.length === 0 && (
        <p className="text-sm text-kleda-text-muted">Geen kleed met "{query}".</p>
      )}

      <div className="space-y-sm">
        {!isLoading &&
          items
            .filter((item) => item.isPrimair)
            .map((item) => (
              <Card key={item.itemId} variant="elevated">
                <CardContent className="py-md flex items-center gap-md">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-sm">
                      <h3 className="font-semibold text-kleda-text-main">{item.itemNaam ?? 'Zonder nummer'}</h3>
                      {item.status && <Badge variant="default" size="sm">{item.status}</Badge>}
                    </div>
                    <p className="text-xs text-kleda-text-muted truncate">
                      {[item.soort, item.afmeting, item.werkOrder?.klantnaam?.split(' ').map((w) => w[0]).join('')]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFilmen(item)}
                    className="shrink-0 px-lg py-sm rounded-kleda-md bg-kleda-primary text-white font-semibold"
                  >
                    Filmen
                  </button>
                  {cameraPilot && <button type="button" onClick={() => setVasteCamera(item)} className="shrink-0 px-3 py-3 border rounded font-semibold">
                    Vaste camera
                  </button>}
                </CardContent>
              </Card>
            ))}
      </div>

      {/* Vandaag voor social */}
      <section>
        <h3 className="font-heading font-bold text-lg text-kleda-text-main mb-sm">Vandaag voor social</h3>
        {vandaag === null && <p className="text-sm text-kleda-text-muted">Laden...</p>}
        {vandaag !== null && vandaag.length === 0 && (
          <p className="text-sm text-kleda-text-muted">Nog niets gemarkeerd of gefilmd vandaag.</p>
        )}
        {vandaag !== null && vandaag.length > 0 && (
          <div className="grid grid-cols-3 gap-sm">
            {vandaag.map((m) => (
              <div key={m.id} className="relative">
                {m.type === 'video' ? (
                  <video src={m.url} preload="metadata" playsInline className="w-full aspect-square rounded-kleda-sm object-cover bg-gray-100" />
                ) : (
                  <img src={m.url} alt="" loading="lazy" className="w-full aspect-square rounded-kleda-sm object-cover bg-gray-100" />
                )}
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white">
                  {m.fase === 'social' ? 'clip' : 'ster'}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
